import { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { FilterInDropdown } from '@sorare/core/src/atoms/dropdowns/FilterInDropdown';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { RadioGroup } from '@sorare/core/src/atoms/inputs/RadioGroup';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';

import { SelectHeader } from 'atoms/SelectHeader';

type Props<T extends Rarity> = {
  rarities: T[];
  selectedRarity: T;
  onChange: (rarity: T) => void;
} & Pick<
  ComponentProps<typeof FilterInDropdown>,
  'buttonColor' | 'fullWidth' | 'buttonSize'
>;

export const SelectRarity = <T extends Rarity>({
  rarities,
  selectedRarity,
  onChange,
  buttonSize = 'small',
  fullWidth = false,
  buttonColor = 'tertiary',
}: Props<T>) => {
  const iconSize = (() => {
    switch (buttonSize) {
      case 'large':
        return 3;
      case 'medium':
        return 2.5;
      default:
      case 'small':
        return 2;
    }
  })();
  const options = rarities.map(r => ({
    label: (
      <Horizontal as="span" gap={2}>
        <ScarcityIcon rarity={r} size={iconSize} />
        <FormattedMessage {...scarcityMessages[r]} />
      </Horizontal>
    ),
    value: r,
  }));

  return (
    <FilterInDropdown
      buttonLabel={<ScarcityIcon rarity={selectedRarity} size={iconSize} />}
      buttonSize={buttonSize}
      buttonColor={buttonColor}
      fullWidth={fullWidth}
    >
      {({ closeDropdown }) => (
        <>
          <SelectHeader>
            <FormattedMessage
              id="SelectRarity.title"
              defaultMessage="Select rarity"
            />
          </SelectHeader>
          <RadioGroup
            modalSelect
            options={options}
            value={selectedRarity}
            name="rarity-selector"
            onChange={rarity => {
              onChange(rarity);
              closeDropdown();
            }}
          />
        </>
      )}
    </FilterInDropdown>
  );
};
